import config from "../../../../packages/config"
import { Link } from "../../../../packages/ui/link"

export const frontmatter = {
  title: "Privacy Policy",
  date: "2026-04-03",
}

### 1. Introduction

This Privacy Policy explains how {config.core.projectName} ("we", "us", or "our") collects, uses, and protects your personal information when you use our website at <Link href={config.core.websiteUrl}>{config.core.websiteUrl}</Link> and our services.

### 2. Information We Collect

We collect information in the following ways:

**Information you provide directly:**

- **Account information** — Email address when you create an account or sign in
- **Payment information** — Billing details when you make a purchase (processed by Polar.sh)
- **Communications** — Information you provide when contacting us for support

**Information collected automatically:**

- **Usage data** — Pages visited, features used, and interactions with the website
- **Device information** — Browser type, operating system, and device identifiers
- **Log data** — IP address, access times, and referring URLs

### 3. How We Use Your Information

We use the information we collect to:

- Provide and maintain our services
- Manage your account
- Send transactional emails (e.g. authentication codes)
- Respond to your support requests
- Improve our website and services
- Detect and prevent fraud or abuse
- Comply with legal obligations

### 4. Third-Party Services

We use third-party services to operate {config.core.projectName}. These services may collect and process your data according to their own privacy policies:

- **Cloudflare** — Website hosting, security, and analytics. [Privacy Policy](https://www.cloudflare.com/privacypolicy/)
- **Polar.sh** — Payment processing. [Privacy Policy](https://polar.sh/legal/privacy)
- **Resend** — Transactional email delivery. [Privacy Policy](https://resend.com/legal/privacy-policy)

### 5. Cookies and Tracking

We use cookies and similar technologies for:

- **Essential cookies** — Required for authentication and session management
- **Analytics** — To understand how visitors use our website (via Cloudflare Web Analytics)

Cloudflare Web Analytics is privacy-focused and does not use cookies or track individual users across sites.

### 6. Data Retention

We retain your personal information for as long as necessary to:

- Provide our services to you
- Comply with legal obligations
- Resolve disputes and enforce our agreements

When you delete your account, we will delete or anonymize your personal information within 30 days, except where we are required to retain it for legal purposes.

### 7. Data Security

We implement appropriate technical and organizational measures to protect your personal information, including:

- Encryption of data in transit (HTTPS/TLS)
- Secure session management with httpOnly cookies
- Bot protection via Cloudflare Turnstile
- Regular security reviews of our codebase

However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security of your data.

### 8. Your Rights

Depending on your location, you may have the following rights regarding your personal information:

- **Access** — Request a copy of the personal information we hold about you
- **Correction** — Request correction of inaccurate or incomplete information
- **Deletion** — Request deletion of your personal information
- **Portability** — Request a copy of your data in a portable format
- **Objection** — Object to processing of your personal information

To exercise these rights, contact us at <Link href={`mailto:${config.core.supportEmail}`}>{config.core.supportEmail}</Link>.

### 9. International Data Transfers

Your information may be transferred to and processed in countries other than your own. We use Cloudflare's global network, which means your data may be processed in multiple locations worldwide. We ensure appropriate safeguards are in place for such transfers.

### 10. Children's Privacy

{config.core.projectName} is not intended for children under 16 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.

### 11. Changes to This Policy

We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on <Link href={config.core.websiteUrl}>{config.core.websiteUrl}</Link> and updating the "Last updated" date. Your continued use of {config.core.projectName} after such changes constitutes acceptance of the updated policy.

### 12. Contact Us

If you have any questions about this Privacy Policy or our data practices, please contact us at <Link href={`mailto:${config.core.supportEmail}`}>{config.core.supportEmail}</Link>.
